import { FC, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { generateCSSFilters, getImageUrl, showImageWhenLoaded, verifyEmsUrl } from '../common/Media';
import useFetchMedia from '../hooks/useFetchMedia';
import { MEDIA_PREFIX, WEBP_SUPPORT } from '../Settings';

interface MediaElementProps {
  media?: any,
  alt?: string,
  dimensions?: string,
  className?: string,
  style?: any,
  onClick?: any,
  setMedia?: any,
  backgroundColor?: string,
  objectFit?: string
}

const MediaWrapper = styled.figure<{ $backgroundColor?: string }>`
  background-color: ${({ $backgroundColor = "#333" }) => $backgroundColor};
  background-repeat: no-repeat;
  background-position: 50% 50%;
  background-size: 25px;
  background-blend-mode: overlay;
  transition: background-color 0.5s;
  margin: 0;
  display: block;
  border-radius: var(--image-radius, 3px);
  flex-shrink: 0;
  overflow: hidden;

  &.link {
    cursor: pointer;
  }
`;

const mediaVisualStyles = `
  opacity: 0;
  transition: opacity 0.5s;
  display: block;
  width: 100%;
  height: 100%;
  visibility: hidden;
  overflow: unset;
`;

const ImageVisual = styled.img<{ $focalX?: number, $focalY?: number, $scale?: number, $filters?: string, $objectFit?: string }>`
  ${mediaVisualStyles}
  object-fit: ${({ $objectFit }) => $objectFit || "cover"};
  filter: ${({ $filters }) => $filters ?? "none"};
  scale: ${({ $scale = 1 }) => $scale};

  ${({ $scale = 1, $focalX = 50, $focalY = 50 }) => $scale === 1 && `object-position: ${$focalX}% ${$focalY}%;`}
  ${({ $scale = 1, $focalX = 50, $focalY = 50 }) => $scale !== 1 && `transform-origin: ${$focalX}% ${$focalY}%;`}
`;

const VideoVisual = styled.video<{ $focalX?: number, $focalY?: number, $scale?: number, $filters?: string, $objectFit?: string }>`
  ${mediaVisualStyles}
  object-fit: ${({ $objectFit }) => $objectFit || "cover"};
  filter: ${({ $filters }) => $filters ?? "none"};
  scale: ${({ $scale = 1 }) => $scale};

  ${({ $scale = 1, $focalX = 50, $focalY = 50 }) => $scale === 1 && `object-position: ${$focalX}% ${$focalY}%;`}
  ${({ $scale = 1, $focalX = 50, $focalY = 50 }) => $scale !== 1 && `transform-origin: ${$focalX}% ${$focalY}%;`}
`;

/**
 * Build a scaled image/poster URL from a media source path.
 * @param sourceUrl Raw media URL without query params
 * @param dimensions Optional fixed dimension string
 * @param elementWidth Measured container width used when dimensions are omitted
 * @returns Fully prefixed URL ready for an img or video poster
 */
const buildScaledImageUrl = (sourceUrl: string, dimensions: string | undefined, elementWidth: number): string | undefined => {
  let imgUrl = String(sourceUrl).replace(/\?.*$/, '');
  if (!imgUrl || imgUrl === "undefined") { return undefined; }

  imgUrl = verifyEmsUrl(imgUrl);

  if (!dimensions) {
    if (elementWidth < 601) {
      imgUrl += `?dimension=600x600`;
    } else if (elementWidth < 801) {
      imgUrl += `?dimension=800x800`;
    } else if (elementWidth < 1201) {
      imgUrl += `?dimension=1200x1200`;
    } else {
      imgUrl += `?dimension=3500`;
    }
  } else {
    imgUrl += `?dimension=${dimensions}`;
  }

  imgUrl = getImageUrl(imgUrl);

  if (!WEBP_SUPPORT && !imgUrl.includes("mediaType")) {
    imgUrl += imgUrl.includes("?") ? `&mediaType=${"image/jpg"}` : `?mediaType=${"image/jpg"}`;
  }

  if (MEDIA_PREFIX && !imgUrl.includes(MEDIA_PREFIX)) {
    imgUrl = imgUrl.replace(/MEDIA_PREFIX/g, "");
  }

  return MEDIA_PREFIX + imgUrl;
};

/**
 * Build a playable video URL with MEDIA_PREFIX applied.
 * @param mediaObject Resolved media object
 * @returns Prefixed video source URL, or undefined if missing
 */
const buildVideoUrl = (mediaObject: any): string | undefined => {
  let videoUrl = String(mediaObject?.url || mediaObject?.src || "");
  if (!videoUrl || videoUrl === "undefined") { return undefined; }

  videoUrl = verifyEmsUrl(videoUrl);

  if (MEDIA_PREFIX && !videoUrl.includes(MEDIA_PREFIX)) {
    videoUrl = videoUrl.replace(/MEDIA_PREFIX/g, "");
  }

  return MEDIA_PREFIX + videoUrl;
};

/**
 * Reveal a media element after it has loaded.
 * @param target Image or video DOM element
 */
const revealMedia = (target: HTMLElement) => {
  target.classList.remove("loading");
  target.style.opacity = "1";
  target.style.visibility = "visible";
};

/**
 * Render an image or muted looping video based on media_type.
 * Supports focalPoint, scale and filters the same way as Image.
 * Unknown or missing media_type defaults to image.
 * @returns {JSX.Element} Component template
 */
const MediaElement: FC<MediaElementProps> = ({
  media,
  alt,
  className,
  dimensions,
  style,
  onClick,
  setMedia,
  backgroundColor,
  objectFit
}) => {
  const wrapperRef = useRef<HTMLElement>(null);
  const [scaledImageUrl, setScaledImageUrl] = useState<string | undefined>();
  const [videoUrl, setVideoUrl] = useState<string | undefined>();
  const [posterUrl, setPosterUrl] = useState<string | undefined>();
  const [mediaObject] = useFetchMedia(media, { dimension: dimensions });
  const [filterCss, setFilterCss] = useState<string | undefined>(undefined);
  const [processedObjectFit, setProcessedObjectFit] = useState<string>("cover");

  const isVideo = mediaObject?.media_type === "video";

  useEffect(() => {
    if (mediaObject?.url && setMedia) {
      setMedia(mediaObject);
    }
  }, [mediaObject, setMedia]);

  useEffect(() => {
    if (mediaObject?.filters) {
      setFilterCss(generateCSSFilters(mediaObject?.filters));
    } else {
      setFilterCss(undefined);
    }
  }, [mediaObject]);

  useEffect(() => {
    const pixelRatio = window?.devicePixelRatio || 1;
    const elementWidth = pixelRatio >= 2
      ? (wrapperRef?.current?.offsetWidth || 600) * 2
      : (wrapperRef?.current?.offsetWidth || 600);

    if (isVideo) {
      setVideoUrl(buildVideoUrl(mediaObject));
      setPosterUrl(buildScaledImageUrl(
        mediaObject?.thumbUrl || mediaObject?.thumbnail_src || "",
        dimensions,
        elementWidth
      ));
      setScaledImageUrl(undefined);
      return;
    }

    const imageSource = mediaObject?.thumbUrl || mediaObject?.url || mediaObject?.src;
    setScaledImageUrl(buildScaledImageUrl(imageSource, dimensions, elementWidth));
    setVideoUrl(undefined);
    setPosterUrl(undefined);
  }, [mediaObject, dimensions, isVideo]);

  const onImageLoad = useCallback((e: React.ChangeEvent<HTMLImageElement>) => {
    if (objectFit === "auto") {
      const ratio = e.target.width / e.target.height;
      const fit = ratio > 1.3 && ratio < 2.2 ? "cover" : "contain";
      setProcessedObjectFit(fit);
    } else {
      setProcessedObjectFit(objectFit || "cover");
    }
    showImageWhenLoaded(e);
  }, [objectFit]);

  const onVideoLoad = useCallback((e: React.SyntheticEvent<HTMLVideoElement>) => {
    setProcessedObjectFit(objectFit === "auto" ? "cover" : (objectFit || "cover"));
    revealMedia(e.currentTarget);
  }, [objectFit]);

  const focalX = mediaObject?.focalPoint?.x;
  const focalY = mediaObject?.focalPoint?.y;
  const scale = mediaObject?.scale || 1;
  const hasMediaSource = Boolean(
    mediaObject?.thumbUrl || mediaObject?.url || mediaObject?.src
  );

  return (
    <MediaWrapper
      ref={wrapperRef}
      onClick={onClick}
      style={style}
      className={className}
      $backgroundColor={backgroundColor}
    >
      {isVideo && videoUrl && hasMediaSource ? (
        <VideoVisual
          className="loading"
          src={videoUrl}
          poster={posterUrl}
          muted
          loop
          autoPlay
          playsInline
          preload="auto"
          onLoadedData={onVideoLoad}
          $focalX={focalX}
          $focalY={focalY}
          $scale={scale}
          $filters={filterCss}
          $objectFit={processedObjectFit}
        />
      ) : scaledImageUrl && hasMediaSource ? (
        <ImageVisual
          alt={alt || ""}
          className="loading"
          onLoad={onImageLoad}
          src={scaledImageUrl}
          $focalX={focalX}
          $focalY={focalY}
          $scale={scale}
          $filters={filterCss}
          $objectFit={processedObjectFit}
        />
      ) : (
        <ImageVisual src="data:," />
      )}
    </MediaWrapper>
  );
};

export default MediaElement;
